/**
 * SelectedFieldDetail — the sticky bottom-of-schema panel (doc 147 §5.4 ·
 * task #14). When a field is selected in the sheet's Schema column, this
 * panel surfaces the description, the references it triggers, and quick
 * copy-to-clipboard for the TypeScript declaration + the entity's Firestore
 * path.
 */
import type { AtlasManifest, Field } from '@atlas-manifest/types';
import { entityById, fieldReferenceTargets } from '../state/selectors.js';
import { DomainBadge } from './DomainBadge.js';

export interface SelectedFieldDetailProps {
  manifest: AtlasManifest;
  entityId: string;
  fieldName: string;
  onNavigateEntity: (entityId: string) => void;
}

/** Pure format helper — `name: type;` with `?` for optional. */
export function formatTypescriptField(field: Pick<Field, 'name' | 'type' | 'optional'>): string {
  return `${field.name}${field.optional ? '?' : ''}: ${field.type};`;
}

export function SelectedFieldDetail({
  manifest,
  entityId,
  fieldName,
  onNavigateEntity,
}: SelectedFieldDetailProps): React.JSX.Element | null {
  const entity = entityById(manifest, entityId);
  const field = entity?.fields.find((f) => f.name === fieldName);
  if (!entity || !field) return null;

  const targets = fieldReferenceTargets(manifest, entityId, fieldName);
  const description = field.description ?? fallbackDescription(field);
  const firestorePath = entity.firestorePath;
  const canCopyPath = Boolean(firestorePath);

  const copy = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard?.writeText(text);
    } catch (err) {
      // Clipboard is best-effort (HTTPS-only, user can deny). Log in dev tools
      // so the silence isn't mysterious; no user-visible recovery.
      console.debug('[atlas] clipboard denied:', err);
    }
  };

  return (
    <div className="sheet-selected-field">
      <div className="sheet-selected-field-label">SELECTED · {field.name}</div>
      <p className="sheet-selected-field-desc">{description}</p>
      {targets.length > 0 && (
        <p className="sheet-selected-field-warn">
          ⚠ Changes ripple to {targets.length} entit{targets.length === 1 ? 'y' : 'ies'} —{' '}
          {targets.map((t, i) => {
            const targetEntity = entityById(manifest, t);
            const targetDomain = targetEntity?.domain ?? '';
            return (
              <span key={t}>
                {i > 0 && ', '}
                <button
                  className="sheet-selected-field-ripple"
                  onClick={() => onNavigateEntity(t)}
                >
                  {t}
                </button>
                {targetDomain && <DomainBadge domainId={targetDomain} size="sm" />}
              </span>
            );
          })}
        </p>
      )}
      <div className="sheet-selected-field-actions">
        <button
          className="btn btn-ghost"
          onClick={() => void copy(formatTypescriptField(field))}
        >
          Copy TS
        </button>
        <button
          className="btn btn-ghost"
          onClick={() => firestorePath && void copy(firestorePath)}
          disabled={!canCopyPath}
        >
          Copy path
        </button>
      </div>
    </div>
  );
}

function fallbackDescription(field: Pick<Field, 'type' | 'referencesEntity' | 'referencesEnum'>): string {
  if (field.referencesEntity) return `Reference to ${field.referencesEntity}.`;
  if (field.referencesEnum) return `Enum value of ${field.referencesEnum}.`;
  return `A ${field.type} field — no description set.`;
}
